import { useEffect, useState } from 'react'
import { useAppSelector } from '../hooks/redux'
import { Issue } from '@/types'
import IssueDetailModal from '../components/IssueDetailModal'
import { useToast } from '../components/ToastContainer'
import {
  TrashIcon,
  ArrowUturnLeftIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  ClockIcon
} from '@heroicons/react/24/outline'
import * as issueApi from '../services/api/issue'

interface TrashIssue extends Issue {
  daysLeft: number
  expiryDate: Date
}

const TrashIssues = () => {
  const { currentProject } = useAppSelector(state => state.project)
  const { user } = useAppSelector(state => state.auth)
  const { showSuccess, showError } = useToast()
  
  const [issues, setIssues] = useState<TrashIssue[]>([])
  const [retentionDays, setRetentionDays] = useState(30)
  const [loading, setLoading] = useState(true)
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN'
  const isSuperAdmin = user?.role === 'SUPER_ADMIN'


  useEffect(() => {
    if (currentProject?.id) {
      loadDeletedIssues()
    }
  }, [currentProject?.id])

  const loadDeletedIssues = async () => {
    if (!currentProject?.id) return
    
    try {
      setLoading(true)
      const response = await issueApi.getDeletedIssues(currentProject.id)
      if (response.success && response.data) {
        const nextRetentionDays = response.data.retentionDays ?? 30
        const normalizedIssues: TrashIssue[] = (response.data.issues ?? []).map((issue) => {
          const deletedAt = issue.deletedAt ? new Date(issue.deletedAt) : new Date()
          const expiryDate = new Date(deletedAt)
          expiryDate.setDate(expiryDate.getDate() + nextRetentionDays)
          const diffTime = expiryDate.getTime() - Date.now()
          const daysLeft = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)))

          return {
            ...issue,
            deletedAt,
            expiryDate,
            daysLeft,
          }
        })

        setIssues(normalizedIssues)
        setRetentionDays(nextRetentionDays)
      }
    } catch (error) {
      console.error('載入回收桶議題失敗:', error)
      showError('載入失敗', '無法載入回收桶議題')
    } finally {
      setLoading(false)
    }
  }

  const handleRestore = async (issue: TrashIssue) => {
    try {
      await issueApi.restoreIssue(issue.id)
      showSuccess('已恢復議題', `議題「${issue.title}」已恢復到看板`)
      await loadDeletedIssues()
    } catch (error) {
      console.error('恢復失敗:', error)
      showError('操作失敗', '無法恢復議題')
    }
  }

  const handlePermanentDelete = async (issue: TrashIssue) => {
    if (!confirm(`確定要永久刪除議題「${issue.title}」嗎？此操作無法復原！`)) {
      return
    }

    try {
      await issueApi.permanentlyDeleteIssue(issue.id)
      showSuccess('已永久刪除', `議題「${issue.title}」已永久刪除`)
      await loadDeletedIssues()
    } catch (error) {
      console.error('永久刪除失敗:', error)
      showError('操作失敗', '無法永久刪除議題')
    }
  }

  const handleIssueClick = (issue: TrashIssue) => {
    setSelectedIssue(issue)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedIssue(null)
  }

  const getDaysLeftColor = (daysLeft: number) => {
    if (daysLeft <= 3) return 'text-red-600 bg-red-50'
    if (daysLeft <= 7) return 'text-yellow-600 bg-yellow-50'
    return 'text-gray-600 bg-gray-50'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* 統計資訊 */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-2xl font-bold text-red-600">{issues.length}</div>
            <div className="text-gray-600 text-sm">回收桶議題</div>
          </div>
          <TrashIcon className="w-8 h-8 text-red-500" />
        </div>
        <div className="mt-3 text-xs text-gray-500">
          保留 {retentionDays} 天後自動永久刪除
        </div>
      </div>

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-600 mt-1">
              {currentProject?.name} - 已刪除的議題將在 {retentionDays} 天後永久刪除
            </p>
          </div>
          <div className="text-sm text-gray-500">
            共 {issues.length} 個已刪除議題
          </div>
        </div>

        {/* Warning */}
        {issues.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-yellow-800">注意事項</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  回收桶中的議題將在指定天數後自動永久刪除。請及時恢復重要議題。
                  {!isSuperAdmin && (
                    <span className="block mt-1 text-xs">
                      註：僅超級管理員可以執行永久刪除操作。
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Issues List */}
        {issues.length === 0 ? (
          <div className="text-center py-12">
            <TrashIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">回收桶是空的</h3>
            <p className="text-gray-500">目前沒有已刪除的議題</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      議題
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      負責人
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      刪除時間
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      剩餘天數
                    </th>
                    {isAdmin && (
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        操作
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {issues.map((issue) => (
                    <tr key={issue.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-start space-x-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="text-xs font-medium text-gray-500">{issue.key}</span>
                              <button
                                onClick={() => handleIssueClick(issue)}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <EyeIcon className="w-4 h-4" />
                              </button>
                            </div>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">
                              {issue.title}
                            </p>
                            {issue.description && (
                              <p className="text-sm text-gray-500 line-clamp-1 mt-1">
                                {issue.description.replace(/<[^>]*>/g, '').substring(0, 100)}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {issue.assignee ? (
                          <div className="flex items-center">
                            {issue.assignee.avatar ? (
                              <img
                                className="w-6 h-6 rounded-full mr-2"
                                src={issue.assignee.avatar}
                                alt={issue.assignee.name}
                              />
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center mr-2">
                                <span className="text-xs font-medium text-gray-600">
                                  {issue.assignee.name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            )}
                            <span className="text-sm text-gray-900">{issue.assignee.name}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">未指派</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {issue.deletedAt ? new Date(issue.deletedAt).toLocaleDateString() : '-'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {issue.deletedAt ? new Date(issue.deletedAt).toLocaleTimeString() : ''}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDaysLeftColor(issue.daysLeft)}`}>
                          <ClockIcon className="w-3 h-3 mr-1" />
                          {issue.daysLeft > 0 ? `${issue.daysLeft} 天` : '即將刪除'}
                        </span>
                      </td>
                      {isAdmin && (
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleRestore(issue)}
                              className="text-green-600 hover:text-green-900 p-1"
                              title="恢復議題"
                            >
                              <ArrowUturnLeftIcon className="w-4 h-4" />
                            </button>
                            {isSuperAdmin && (
                              <button
                                onClick={() => handlePermanentDelete(issue)}
                                className="text-red-600 hover:text-red-900 p-1"
                                title="永久刪除（僅超級管理員）"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Issue Detail Modal */}
        {selectedIssue && (
          <IssueDetailModal
            issue={selectedIssue}
            isOpen={isModalOpen}
            onClose={handleCloseModal}
          />
        )}
      </div>
    </div>
  )
}

export default TrashIssues
